"use client";

import { motion, useReducedMotion } from "framer-motion";
import { useEffect, useState } from "react";

import { cn } from "@/lib/utils";

import { CursorPointer, RetroMouse } from "./retro-computer";

/**
 * Building blocks for the animated terminal screen: a step-machine hook that
 * drives the choreography, the on-screen cursor, click rings, and a type-on
 * line. Coordinates are in screen pixels (origin top-left of the screen
 * surface, 320x200).
 */

export interface ArtState {
  /** Index of the active step in the script. */
  i: number;
  /** Completed loop count, used to re-key one-shot animations. */
  loop: number;
  reduced: boolean;
}

/**
 * Advances through `durations` (ms per step) forever. With reduced motion the
 * script freezes on `showcaseIndex` so the scene reads as a finished still.
 */
export function useArtScript(
  durations: readonly number[],
  showcaseIndex: number,
): ArtState {
  const reduced = Boolean(useReducedMotion());
  const [state, setState] = useState({ i: 0, loop: 0 });

  useEffect(() => {
    if (reduced) {
      return;
    }
    const timer = setTimeout(() => {
      setState((s) => {
        const next = (s.i + 1) % durations.length;
        return { i: next, loop: next === 0 ? s.loop + 1 : s.loop };
      });
    }, durations[state.i]);
    return () => {
      clearTimeout(timer);
    };
  }, [state, reduced, durations]);

  return reduced
    ? { i: showcaseIndex, loop: 0, reduced: true }
    : { ...state, reduced: false };
}

export function ScreenCursor({
  x,
  y,
  pressed = false,
  hidden = false,
}: {
  x: number;
  y: number;
  pressed?: boolean;
  hidden?: boolean;
}) {
  return (
    <motion.div
      className="pointer-events-none absolute top-0 left-0 z-50"
      initial={false}
      animate={{ x, y, scale: pressed ? 0.85 : 1, opacity: hidden ? 0 : 1 }}
      transition={{ type: "spring", stiffness: 130, damping: 17, mass: 0.7 }}
    >
      <CursorPointer />
    </motion.div>
  );
}

/** Expanding ring marking a click; re-key per step so it replays each loop. */
export function ClickRing({
  x,
  y,
  tone = "blue",
  delay = 0.45,
}: {
  x: number;
  y: number;
  tone?: "blue" | "cream";
  delay?: number;
}) {
  return (
    <motion.span
      className={cn(
        "pointer-events-none absolute z-40 rounded-full border-2",
        tone === "blue" ? "border-marketing-blue" : "border-marketing-cream",
      )}
      style={{ left: x - 11, top: y - 11, width: 22, height: 22 }}
      initial={{ transform: "scale(0.2)", opacity: 0 }}
      animate={{
        transform: ["scale(0.2)", "scale(0.6)", "scale(1.15)"],
        opacity: [0, 0.9, 0],
      }}
      transition={{ duration: 0.6, delay, times: [0, 0.4, 1] }}
    />
  );
}

const TYPE_INTERVAL_MS = 26;

/** Terminal line that types on when active; mount fresh (key by loop) to replay. */
export function TypeLine({
  text,
  mode,
  className,
}: {
  text: string;
  mode: "hidden" | "typing" | "done";
  className?: string;
}) {
  const [shown, setShown] = useState(mode === "done" ? text.length : 0);

  useEffect(() => {
    if (mode !== "typing") {
      setShown(mode === "done" ? text.length : 0);
      return;
    }
    setShown(0);
    const timer = setInterval(() => {
      setShown((n) => {
        if (n >= text.length) {
          clearInterval(timer);
          return n;
        }
        return n + 1;
      });
    }, TYPE_INTERVAL_MS);
    return () => {
      clearInterval(timer);
    };
  }, [mode, text]);

  if (mode === "hidden") {
    return null;
  }

  return (
    <p className={cn("font-mono text-[11px] leading-[22px]", className)}>
      {text.slice(0, shown)}
      {mode === "typing" ? <span className="animate-pulse">▌</span> : null}
    </p>
  );
}

/** Desk mouse that subtly mirrors the on-screen cursor, selling "real input". */
export function MirroredMouse({
  x,
  y,
  className,
}: {
  x: number;
  y: number;
  className?: string;
}) {
  return (
    <motion.div
      className={className}
      initial={false}
      animate={{ x: (x - 160) * 0.05, y: (y - 100) * 0.05 }}
      transition={{ type: "spring", stiffness: 60, damping: 14 }}
    >
      <RetroMouse />
    </motion.div>
  );
}
